import { capitalize } from 'lodash';
import type { IntlShape } from 'react-intl';

import { isActiveQuery } from '~/features/advanced-search/query-filters/QueryFilters.utils';
import { SearchQuery } from '~/features/advanced-search/types';

import { LibraryModelVersion, Queries } from '../types';

export const getModelTitle = (label: string, models: LibraryModelVersion[]) => {
  if (models.length === 1) {
    return `${capitalize(label)} (${models.length} model)`;
  } else {
    return `${capitalize(label)} (${models.length} models)`;
  }
};

export const updateAllModelsGlobalQuery = ({
  globalQuery,
  modelsInCart,
}: {
  globalQuery: SearchQuery;
  modelsInCart: LibraryModelVersion[];
}) => {
  return modelsInCart.reduce((acc, model) => {
    const id = model.libraryModelId;
    const activeQuery = isActiveQuery(globalQuery);

    acc[id] = activeQuery;
    return acc;
  }, {} as Queries);
};

export const updateAllModelsCustomQuery = ({
  categoryQuery,
  categoryModels,
}: {
  categoryQuery: Record<string, SearchQuery>;
  categoryModels: Map<string, LibraryModelVersion[]>;
}) => {
  const result: Queries = {};

  categoryModels.forEach((models, label) => {
    const selectedQuery = categoryQuery?.[label] || [];

    return models.forEach((model) => {
      const id = model.libraryModelId;
      const activeQuery = isActiveQuery(selectedQuery);

      result[id] = activeQuery;
    });
  });

  return result;
};

export const OPERATORS_WITH_NO_VALUES = new Set([
  'is_blank',
  'is_not_blank',
  'is_true',
  'is_false',
]);

export const getPublishingModalTitle = ({
  allModelsFailed,
  publishingModels,
  isCreatingTestCases,
  intl,
  isDraft,
  modelsInCartCount,
}: {
  intl: IntlShape;
  isDraft: boolean;
  isCreatingTestCases: boolean;
  allModelsFailed: boolean;
  publishingModels: LibraryModelVersion[];
  modelsInCartCount: number;
}) => {
  return allModelsFailed
    ? isDraft
      ? intl.formatMessage(
          {
            id: 'ModelAddScope.saveErrorTitle',
            defaultMessage:
              '{count, plural, one {Error} other {Errors}} saving {count, plural, one {model} other {models}}',
            description:
              'Title when there are errors saving models with pluralization. Example: count = 3',
          },
          {
            count: modelsInCartCount,
          },
        )
      : intl.formatMessage(
          {
            id: 'ModelAddScope.publishErrorTitle',
            defaultMessage:
              '{count, plural, one {Error} other {Errors}} publishing {count, plural, one {model} other {models}}',
            description:
              'Title when there are errors publishing models with pluralization. Example: count = 3',
          },
          {
            count: modelsInCartCount,
          },
        )
    : publishingModels.every((model) => model.publishState !== 'pending') &&
        !isCreatingTestCases
      ? isDraft
        ? intl.formatMessage(
            {
              id: 'ModelAddScope.savedTitle',
              defaultMessage:
                '{count, plural, one {Model} other {Models}} saved',
              description:
                'Title when models are successfully saved with pluralization. Example: count = 3',
            },
            {
              count: modelsInCartCount,
            },
          )
        : intl.formatMessage(
            {
              id: 'ModelAddScope.publishedTitle',
              defaultMessage:
                '{count, plural, one {Model} other {Models}} published',
              description:
                'Title when models are successfully published with pluralization. Example: count = 3',
            },
            {
              count: modelsInCartCount,
            },
          )
      : isDraft
        ? intl.formatMessage(
            {
              id: 'ModelAddScope.savingTitle',
              defaultMessage:
                'Saving {count, plural, one {model} other {models}}…',
              description:
                'Title while saving models with pluralization. Example: count = 2',
            },
            {
              count: modelsInCartCount,
            },
          )
        : intl.formatMessage(
            {
              id: 'ModelAddScope.publishingTitle',
              defaultMessage:
                'Publishing {count, plural, one {model} other {models}}…',
              description:
                'Title while publishing models with pluralization. Example: count = 2',
            },
            {
              count: modelsInCartCount,
            },
          );
};
